import React from "react";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../../utils/countryLocaleMap";
import type { PreOrderItem } from "../../../common/jsonRpcTypes";
import type { BookingTypeGroup } from "../../utils/bookingTypeGroups";
import ItemOptionsDisplay from "./ItemOptionsDisplay";

interface BookingTypeGroupSummaryProps {
  /** One entry per booking type of a linked booking. */
  groups: BookingTypeGroup[];
  currency: string;
  countryCode: string;
  /** Menus whose prices the venue hides from the guest, keyed by menu id. */
  hidePricesByMenu?: Record<string, boolean>;
  /** Hides every price, regardless of menu. */
  shouldHidePrices?: boolean;
  /** Heading level used for each booking type, so the surrounding page keeps a valid heading order. */
  headingLevel?: "h4" | "h5" | "h6";
  className?: string;
}

/** Build a key that stays stable across re-renders without relying on the array index. */
function itemKey(item: PreOrderItem): string {
  const options = (item.options ?? [])
    .map((option) => `${option.label}:${option.values.map((value) => value.label).join("/")}`)
    .join("|");
  return `${item.id}-${item.menu_id}-${item.for_name_index ?? ""}-${options}`;
}

interface GroupItemProps {
  item: PreOrderItem;
  hidePrice: boolean;
  currency: string;
  countryCode: string;
}

/** One line of a booking type's pre-order: name, quantity, guest and chosen options. */
const GroupItemLine: React.FC<Readonly<GroupItemProps>> = ({ item, hidePrice, currency, countryCode }) => {
  const { t } = useTranslation();

  return (
    <>
      <span>
        {item.name}
        {item.quantity > 1 && ` × ${item.quantity}`}
        {item.for_name && ` (${t("for", "for")} ${item.for_name})`}
        <ItemOptionsDisplay
          options={item.options}
          currency={currency}
          countryCode={countryCode}
          hidePrices={hidePrice}
        />
      </span>
      {!hidePrice && <span>{formatCurrency(item.total ?? 0, currency, countryCode)}</span>}
    </>
  );
};

/**
 * A pre-ordered entry, which is either a single item or a fixed-price menu.
 *
 * A fixed-price menu is stored as a container whose nested items hold what the
 * guest actually chose, so those are listed beneath the menu line — otherwise
 * only the menu name is shown and the guest's configurable options are lost.
 * The container carries the price set by the menu's fixed-price rule, so the
 * nested items are listed without prices of their own.
 */
const GroupItemEntry: React.FC<Readonly<GroupItemProps>> = ({ item, hidePrice, currency, countryCode }) => {
  const nestedItems = item.type === "menu" && Array.isArray(item.items) ? item.items : [];

  if (nestedItems.length === 0) {
    return (
      <li className="gcw:flex gcw:justify-between gcw:text-body gcw:text-text-secondary gcw:mb-1">
        <GroupItemLine item={item} hidePrice={hidePrice} currency={currency} countryCode={countryCode} />
      </li>
    );
  }

  return (
    <li className="gcw:text-body gcw:text-text-secondary gcw:mb-1">
      <div className="gcw:flex gcw:justify-between">
        <GroupItemLine item={item} hidePrice={hidePrice} currency={currency} countryCode={countryCode} />
      </div>
      <ul className="gcw:list-none gcw:p-0 gcw:m-0 gcw:ps-bs-3">
        {nestedItems.map((nestedItem, nestedIdx) => (
          <li key={`${itemKey(nestedItem)}-${nestedIdx}`} className="gcw:flex gcw:justify-between">
            <GroupItemLine item={nestedItem} hidePrice currency={currency} countryCode={countryCode} />
          </li>
        ))}
      </ul>
    </li>
  );
};

/**
 * A menu's own setting hides its prices, and `shouldHidePrices` hides every price —
 * `getHidePricesByMenu` records an explicit `false` per menu, so the global flag must
 * be combined with, not defaulted to, the per-menu value.
 */
function hidesPrice(item: PreOrderItem, hidePricesByMenu?: Record<string, boolean>, shouldHidePrices?: boolean) {
  return shouldHidePrices === true || hidePricesByMenu?.[item.menu_id] === true;
}

function groupHeading(group: BookingTypeGroup, fallbackName: string): string {
  const name = group.bookingTypeName ?? fallbackName;
  return group.time ? `${name} · ${group.time}` : name;
}

/**
 * Renders the pre-ordered items and totals of each booking type of a linked
 * booking (e.g. Lunch then Dinner), so the guest can see what they ordered
 * against each sitting rather than a single merged list.
 */
const BookingTypeGroupSummary: React.FC<Readonly<BookingTypeGroupSummaryProps>> = ({
  groups,
  currency,
  countryCode,
  hidePricesByMenu,
  shouldHidePrices,
  headingLevel: Heading = "h5",
  className,
}) => {
  const { t } = useTranslation();

  if (groups.length === 0) return null;

  const fallbackName = t("booking_type", "Booking Type");

  return (
    <div className={className}>
      {groups.map((group) => {
        const hasHiddenPrice = group.items.some((item) => hidesPrice(item, hidePricesByMenu, shouldHidePrices));
        return (
          <section key={group.bookingTypeId} className="gcw:mb-bs-3 gcw:last:mb-0">
            <Heading className="gcw:text-body gcw:font-semibold gcw:text-text-primary gcw:mb-1">
              {groupHeading(group, fallbackName)}
            </Heading>
            <ul className="gcw:list-none gcw:p-0 gcw:m-0">
              {group.items.map((item) => (
                <GroupItemEntry
                  key={itemKey(item)}
                  item={item}
                  hidePrice={hidesPrice(item, hidePricesByMenu, shouldHidePrices)}
                  currency={currency}
                  countryCode={countryCode}
                />
              ))}
            </ul>
            {!hasHiddenPrice && group.serviceChargeAmount > 0 && (
              <div className="gcw:flex gcw:justify-between gcw:text-body gcw:text-text-secondary gcw:mb-1">
                <span>{t("service_charge", "Service Charge")}</span>
                <span>{formatCurrency(group.serviceChargeAmount, currency, countryCode)}</span>
              </div>
            )}
            {!hasHiddenPrice && (
              <div className="gcw:flex gcw:justify-between gcw:text-body gcw:font-semibold gcw:text-text-primary">
                <span>{t("subtotal", "Subtotal")}</span>
                <span>{formatCurrency(group.total, currency, countryCode)}</span>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default BookingTypeGroupSummary;
